"use client";

import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatMiles, formatDuration } from "@/lib/geo-utils";
import { Car, Clock, TrendingUp, CheckCircle, Link as LinkIcon } from "lucide-react";
import { LinkShiftDialog } from "./link-shift-dialog";

interface ShiftSummaryDialogProps {
  shiftId: Id<"shifts"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShiftSummaryDialog({
  shiftId,
  open,
  onOpenChange,
}: ShiftSummaryDialogProps) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const shift = useQuery(
    api.shifts.getShift,
    shiftId ? { id: shiftId } : "skip"
  );

  // Show link dialog for orphan shifts after summary is shown
  useEffect(() => {
    if (shift && !shift.blockId && shift.status === "completed" && open) {
      // Small delay then offer to link
      const timer = setTimeout(() => setShowLinkDialog(true), 500);
      return () => clearTimeout(timer);
    }
  }, [shift, open]);

  // Reset link dialog when main dialog closes
  useEffect(() => {
    if (!open) {
      setShowLinkDialog(false);
    }
  }, [open]);

  if (!shift) {
    return null;
  }

  const duration = shift.endedAt
    ? shift.endedAt - shift.startedAt - (shift.pauseDuration || 0)
    : 0;
  const hours = duration / (1000 * 60 * 60);
  const avgSpeed = hours > 0 ? shift.totalMiles / hours : 0;
  const isOrphan = !shift.blockId && shift.status === "completed";

  return (
    <>
      <Dialog open={open && !showLinkDialog} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Route Complete
            </DialogTitle>
            <DialogDescription>
              Here&apos;s your route summary for{" "}
              {new Date(shift.startedAt).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Main stat */}
            <div className="flex flex-col items-center justify-center rounded-lg bg-muted p-6">
              <Car className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-4xl font-bold tabular-nums">
                {formatMiles(shift.totalMiles)}
              </span>
              <span className="text-sm text-muted-foreground">Total Distance</span>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-semibold">{formatDuration(duration)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-lg border p-3">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Avg Speed</p>
                  <p className="font-semibold">{avgSpeed.toFixed(1)} mph</p>
                </div>
              </div>
            </div>

            {/* Time range */}
            <div className="text-center text-sm text-muted-foreground">
              {new Date(shift.startedAt).toLocaleTimeString()} -{" "}
              {shift.endedAt
                ? new Date(shift.endedAt).toLocaleTimeString()
                : "In progress"}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            {isOrphan && (
              <Button variant="outline" onClick={() => setShowLinkDialog(true)}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Link to Block
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {shiftId && (
        <LinkShiftDialog
          shiftId={shiftId}
          open={showLinkDialog}
          onOpenChange={(isOpen) => {
            setShowLinkDialog(isOpen);
            if (!isOpen) {
              onOpenChange(false);
            }
          }}
        />
      )}
    </>
  );
}
